import { focusManager } from "./FocusManager";
import { isIE } from "./html";
import { anyFunction } from "./types";

function getHotKeyCode(code: string): string {
	const matches = code.toLowerCase().match(/\w+/g);
	let comp = 0;
	let key = "";
	for (let i = 0; i < matches.length; i++) {
		const check = matches[i];
		if (check === "ctrl") {
			comp += 4;
		} else if (check === "shift") {
			comp += 2;
		} else if (check === "alt") {
			comp += 1;
		} else {
			key = check;
		}
	}
	return comp + key;
}

const ie_key_map = {
	Up: "arrowUp",
	Down: "arrowDown",
	Right: "arrowRight",
	Left: "arrowLeft",
	Esc: "escape",
	Spacebar: "space",
};

interface IKeyStorage {
	[key: string]: { handler: (e: Event) => any }[];
}

export interface IKeyManager {
	destructor(): void;
	addHotKey(key: string, handler: any): void;
	removeHotKey(key?: string, context?: any): void;
	exist(key: string): boolean;
	getKeyStorageLength(): number;
}

export class KeyManager implements IKeyManager {
	private _keysStorage: IKeyStorage = {};
	private _beforeCall: (e: Event, focus: any) => boolean;

	private _initHandler = (e: KeyboardEvent) => {
		let key;
		if ((e.which >= 48 && e.which <= 57) || (e.which >= 65 && e.which <= 90)) {
			key = String.fromCharCode(e.which);
		} else {
			const keyName = e.which === 32 ? e.code : e.key;
			key = isIE() ? ie_key_map[keyName] || keyName : keyName;
		}

		const actions = this._keysStorage[
			(e.ctrlKey || e.metaKey ? 4 : 0) +
				(e.shiftKey ? 2 : 0) +
				(e.altKey ? 1 : 0) +
				(key && key.toLowerCase())
		];

		if (actions) {
			for (let i = 0; i < actions.length; i++) {
				if (this._beforeCall && this._beforeCall(e, focusManager.getFocusId()) === false) {
					return;
				}
				actions[i].handler(e);
			}
		}
	};

	constructor(beforeCall?: (e: Event, focus: any) => boolean) {
		if (beforeCall) {
			this._beforeCall = beforeCall;
		}
		document.addEventListener("keydown", this._initHandler);
	}

	destructor() {
		document.removeEventListener("keydown", this._initHandler);
		this.removeHotKey();
	}

	addHotKey(key: string, handler): void {
		const code = getHotKeyCode(key);
		if (!this._keysStorage[code]) {
			this._keysStorage[code] = [];
		}
		this._keysStorage[code].push({ handler });
	}

	removeHotKey(key?: string, handler?: anyFunction): void {
		if (key) {
			if (key && handler) {
				const code = getHotKeyCode(key);
				const functionToString = fun => {
					return fun
						.toString()
						.replace(/\n/g, "")
						.replace(/\s/g, "");
				};

				this._keysStorage[code].forEach((existHotKey, i) => {
					if (functionToString(existHotKey.handler) === functionToString(handler)) {
						delete this._keysStorage[code][i];
						this._keysStorage[code] = this._keysStorage[code].filter(el => el);
					}
				});
			} else {
				const code = getHotKeyCode(key);
				delete this._keysStorage[code];
			}
		} else {
			this._keysStorage = {};
		}
	}

	exist(key: string): boolean {
		const code = getHotKeyCode(key);
		return !!this._keysStorage[code];
	}

	getKeyStorageLength(): number {
		return Object.keys(this._keysStorage).length;
	}
}
